import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Leaf, Wind, Droplets, ShieldCheck, IndianRupee, Boxes, Truck, Sparkles,
  Phone, ArrowRight, Star, ChevronDown, MessageCircle,
} from "lucide-react";
import { api, CONTACT, whatsappLink } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import { useSEO } from "@/lib/seo";
import { Reveal, MaskLines } from "@/components/Reveal";
import { LeafField } from "@/components/LeafDecor";
import { SectionHeader } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const TRUST = ["Premium Quality", "Hygienically Packed", "Strong Aroma", "Consistent Taste"];

const WHY = [
  { icon: Leaf, t: "Premium Tea Leaves", d: "Carefully sourced from India's finest gardens." },
  { icon: Wind, t: "Rich Aroma", d: "A fragrance that fills the room, cup after cup." },
  { icon: Droplets, t: "Strong Liquor", d: "Deep colour and full-bodied strength." },
  { icon: ShieldCheck, t: "Hygienically Processed", d: "Clean, safe and food-grade packing." },
  { icon: IndianRupee, t: "Competitive Pricing", d: "Wholesale rates that protect your margins." },
  { icon: Boxes, t: "Bulk Supply", d: "Flexible MOQ and large-volume capacity." },
  { icon: Truck, t: "Timely Delivery", d: "Fast, reliable dispatch across India." },
  { icon: Sparkles, t: "Customized Blends", d: "Tailored blends for your brand & taste." },
];

const SERVE = [
  "Hotels", "Restaurants", "Tea Cafes", "Tea Stalls", "Hospitals",
  "Corporate Cafeterias", "Catering Services", "Hostels", "Retail Stores",
  "Supermarkets", "Wholesale Distributors",
];

const PROCESS = [
  { n: "01", t: "Tea Sourcing", d: "We handpick quality leaves from trusted estates across India." },
  { n: "02", t: "Quality Testing", d: "Every batch is evaluated for aroma, strength and purity." },
  { n: "03", t: "Expert Blending", d: "Master blenders craft rich, consistent flavour profiles." },
  { n: "04", t: "Hygienic Packing", d: "Sealed in food-grade, moisture-proof packaging." },
  { n: "05", t: "Fast Delivery", d: "Dispatched quickly and reliably, pan-India." },
];

const FAQS = [
  { q: "What is your MOQ?", a: "Our minimum order quantity starts at just 10 kg for most blends and 25 kg for hotel-grade powder. We offer flexible MOQ options depending on your business needs." },
  { q: "Can you customize blends?", a: "Yes. We create tailored blends and custom packaging to match your brand's taste profile and identity." },
  { q: "Do you supply across India?", a: "Absolutely. Leaf Alchemy dispatches bulk tea powder to hotels, restaurants and distributors across India." },
  { q: "How quickly do you deliver?", a: "Most orders are dispatched within 2–4 business days, with delivery timelines depending on your location." },
  { q: "Can I become a distributor?", a: "Yes! We welcome distributor partnerships. Visit our 'Become a Distributor' page to apply." },
];

export default function Home() {
  useSEO({});
  const { openQuote } = useQuote();
  const heroRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [tIndex, setTIndex] = useState(0);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  useEffect(() => {
    api.get("/products")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setProducts(list.filter((p) => !p.future).slice(0, 5));
      })
      .catch(() => setProducts([]));

    api.get("/testimonials")
      .then((r) => setTestimonials(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTestimonials([]));
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const id = setInterval(() => setTIndex((i) => (i + 1) % testimonials.length), 5000);
    return () => clearInterval(id);
  }, [testimonials]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden grain">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1522747776116-64ee03be1dad?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="Tea plantation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-forest/95 via-forest/50 to-forest/30" />
        </motion.div>

        <motion.div style={{ y: contentY }} className="relative max-w-7xl mx-auto px-5 lg:px-8 pb-24 pt-40 w-full">
          <motion.span
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="inline-block text-gold text-xs font-semibold tracking-[0.3em] uppercase mb-6"
          >
            Premium Tea Powder • Crafted in India
          </motion.span>
          <h1 className="font-display text-cream text-[2.6rem] sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl tracking-tight">
            <MaskLines lines={["Every Cup Begins", "with Pure Leaves."]} delay={0.3} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
            className="mt-6 text-cream/85 text-lg max-w-xl"
          >
            Premium Tea Powder Crafted for Hotels, Restaurants, Tea Outlets and Tea Lovers.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Button onClick={() => openQuote()} data-testid="hero-quote-btn"
              className="rounded-full bg-gold hover:bg-cream text-ink h-13 px-8 py-6 text-base font-medium transition-colors">
              Get Wholesale Quote <ArrowRight size={18} className="ml-1" />
            </Button>
            <a href={`tel:${CONTACT.phoneRaw}`} data-testid="hero-call-btn">
              <Button variant="outline" className="rounded-full border-cream/40 bg-cream/5 text-cream hover:bg-cream hover:text-forest h-13 px-8 py-6 text-base backdrop-blur-sm transition-colors">
                <Phone size={18} className="mr-1" /> Call Now
              </Button>
            </a>
          </motion.div>

          {/* trust badges */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
          >
            {TRUST.map((t) => (
              <div key={t} className="flex items-center gap-2 text-cream/90 text-sm">
                <span className="w-5 h-5 rounded-full bg-gold/20 grid place-items-center text-gold">✓</span>
                {t}
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-cream/60"
        >
          <ChevronDown size={26} />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="bg-forest py-5 border-y border-gold/20">
        <Marquee speed={40} gradient={false} autoFill>
          {["Premium Quality", "Hygienically Packed", "Strong Aroma", "Consistent Taste", "Bulk Supply Across India"].map((w, i) => (
            <span key={i} className="font-display text-cream text-2xl sm:text-3xl mx-8 flex items-center gap-8">
              {w} <Leaf className="w-5 h-5 text-gold" fill="currentColor" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* ABOUT */}
      <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="home-about">
        <LeafField />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center relative">
          <Reveal>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1491497895121-1334fc14d8c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Tea garden"
                className="rounded-t-[8rem] rounded-b-2xl w-full h-[520px] object-cover shadow-[0_20px_60px_rgba(15,81,50,0.2)]"
              />
              <div className="absolute -bottom-6 -right-4 sm:right-6 bg-cream border border-gold/30 rounded-2xl px-6 py-4 shadow-xl">
                <p className="font-display text-4xl text-forest">100%</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Natural Leaves</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <span className="text-olive text-xs font-semibold tracking-[0.25em] uppercase">About Leaf Alchemy</span>
            <h2 className="font-display text-4xl sm:text-5xl text-forest mt-4 leading-[1.05]">
              Crafted by Nature. Perfected for Every Cup.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Leaf Alchemy is committed to delivering premium-quality tea powder with exceptional taste and
              consistency. We carefully source quality tea leaves and blend them to create rich flavors loved
              by tea professionals and households alike.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Whether serving thousands of customers in restaurants or brewing tea at home, Leaf Alchemy
              guarantees freshness, aroma and satisfaction.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <div className="border-l-2 border-gold pl-4">
                <h4 className="font-display text-xl text-forest">Our Mission</h4>
                <p className="text-sm text-muted-foreground mt-1">Deliver premium tea experiences through quality, consistency and innovation.</p>
              </div>
              <div className="border-l-2 border-gold pl-4">
                <h4 className="font-display text-xl text-forest">Our Vision</h4>
                <p className="text-sm text-muted-foreground mt-1">Become India&rsquo;s most trusted premium tea powder brand.</p>
              </div>
            </div>
            <Link to="/about"><Button variant="link" className="text-forest px-0 mt-6" data-testid="about-more-btn">Read our story <ArrowRight size={16} className="ml-1" /></Button></Link>
          </Reveal>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-24 lg:py-32 bg-white/40" data-testid="home-products">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader eyebrow="Our Collection" title="Signature Tea Powders" center
            subtitle="Blended for strength, aroma and consistency — trusted by tea professionals across India." />
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <div className="group bg-cream rounded-2xl overflow-hidden border border-forest/10 hover:border-gold/40 transition-colors shadow-[0_8px_32px_rgba(15,81,50,0.06)] hover:shadow-[0_16px_48px_rgba(200,164,77,0.18)]" data-testid={`product-card-${p.slug}`}>
                  <div className="overflow-hidden h-60 rounded-b-[3rem]">
                    <img src={p.images?.[0]} alt={p.name} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-6">
                    <span className="text-[11px] uppercase tracking-widest text-olive">{p.category}</span>
                    <h3 className="font-display text-2xl text-forest mt-1">{p.name}</h3>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.description}</p>
                    <div className="flex gap-2 mt-5">
                      <Button onClick={() => openQuote({ product: p.name })} data-testid={`product-quote-${p.slug}`}
                        className="rounded-full bg-forest hover:bg-olive text-cream flex-1 transition-colors">Request Quote</Button>
                      <a href={whatsappLink(`Hi, I'm interested in ${p.name}. Please share wholesale details.`)} target="_blank" rel="noreferrer"
                        className="w-11 h-11 shrink-0 grid place-items-center rounded-full bg-[#25D366] text-white" data-testid={`product-wa-${p.slug}`}>
                        <MessageCircle size={18} />
                      </a>
                    </div>
                    <Link to={`/products/${p.slug}`} className="block text-center text-sm text-forest/70 hover:text-gold mt-3 transition-colors">View details →</Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/products"><Button variant="outline" className="rounded-full border-forest text-forest hover:bg-forest hover:text-cream px-8 transition-colors" data-testid="view-all-products">View All Products</Button></Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="home-why">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader eyebrow="Why Choose Us" title="The Leaf Alchemy Difference" center />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.05}>
                <div className="h-full bg-cream border border-forest/10 rounded-2xl p-7 hover:-translate-y-1 hover:border-gold/40 transition-[transform,border-color] duration-300" data-testid={`why-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-forest/10 grid place-items-center text-forest mb-4">
                    <w.icon size={24} />
                  </div>
                  <h3 className="font-display text-xl text-forest">{w.t}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-24 lg:py-32 bg-forest text-cream relative overflow-hidden" data-testid="home-serve">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader eyebrow="Who We Serve" title="Trusted Across Industries" light
            subtitle="From bustling tea stalls to five-star hotels — we supply them all." />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {SERVE.map((s, i) => (
              <Reveal key={s} delay={i * 0.04}>
                <div className="border border-cream/15 rounded-xl p-5 hover:bg-cream/5 hover:border-gold/50 transition-colors flex items-center gap-3" data-testid={`serve-${i}`}>
                  <Leaf className="w-4 h-4 text-gold shrink-0" fill="currentColor" />
                  <span className="text-sm font-medium">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS / MANIFESTO */}
      <section className="py-24 lg:py-32" data-testid="home-process">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader eyebrow="Our Process" title="From Leaf to Cup" center
            subtitle="Five deliberate steps that guarantee freshness, aroma and consistency." />
          <div className="space-y-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.06}>
                <div className="group relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10 border-b border-forest/10 py-8 hover:pl-3 transition-[padding]" data-testid={`process-${i}`}>
                  <span className="font-display text-6xl sm:text-8xl text-forest/10 group-hover:text-gold/40 transition-colors leading-none w-28 shrink-0">{p.n}</span>
                  <div className="flex-1">
                    <h3 className="font-display text-2xl sm:text-3xl text-forest">{p.t}</h3>
                    <p className="text-muted-foreground mt-1 max-w-xl">{p.d}</p>
                  </div>
                  <ArrowRight className="hidden sm:block text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-gold/15 py-16" data-testid="home-trust">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
          {[
            { t: "FSSAI Licensed", d: "Food-safety compliant" },
            { t: "GST Registered", d: "Fully compliant billing" },
            { t: "Hygienic Manufacturing", d: "Clean, safe processing" },
            { t: "Premium Quality", d: "Handpicked leaves" },
            { t: "Bulk Supply", d: "Across India" },
          ].map((x, i) => (
            <Reveal key={x.t} delay={i * 0.05}>
              <div data-testid={`trust-${i}`}>
                <ShieldCheck className="mx-auto text-forest mb-2" size={30} />
                <p className="font-display text-lg text-forest leading-tight">{x.t}</p>
                <p className="text-xs text-muted-foreground mt-1">{x.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="home-testimonials">
        <LeafField />
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center relative">
          <SectionHeader eyebrow="Testimonials" title="Loved by Tea Professionals" center />
          {testimonials.length > 0 && (
            <div className="relative min-h-[220px]">
              {testimonials.map((t, i) => (
                <motion.div
                  key={t.id}
                  animate={{ opacity: i === tIndex ? 1 : 0, scale: i === tIndex ? 1 : 0.96 }}
                  transition={{ duration: 0.6 }}
                  className={`${i === tIndex ? "relative" : "absolute inset-0 pointer-events-none"}`}
                  data-testid={`testimonial-${i}`}
                >
                  <div className="flex justify-center gap-1 mb-5">
                    {Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={18} className="text-gold" fill="currentColor" />)}
                  </div>
                  <p className="font-display text-2xl sm:text-3xl text-forest leading-snug italic">{`"${t.quote}"`}</p>
                  <p className="mt-6 font-semibold text-forest">{t.name}</p>
                  <p className="text-sm text-muted-foreground">{t.role}{t.company ? `, ${t.company}` : ""}</p>
                </motion.div>
              ))}
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, i) => (
                  <button key={i} onClick={() => setTIndex(i)} data-testid={`testimonial-dot-${i}`}
                    className={`h-2 rounded-full transition-all ${i === tIndex ? "w-8 bg-gold" : "w-2 bg-forest/20"}`} aria-label={`Testimonial ${i + 1}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-white/40" data-testid="home-faq">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <SectionHeader eyebrow="FAQ" title="Questions, Answered" center />
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-forest/10" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-display text-lg text-forest hover:text-gold hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden bg-forest" data-testid="home-cta">
        <LeafField />
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center relative">
          <h2 className="font-display text-4xl sm:text-5xl text-cream leading-tight">Ready to Serve the Perfect Cup?</h2>
          <p className="text-cream/75 mt-4">Get a customized wholesale quote for your business today.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button onClick={() => openQuote()} data-testid="cta-quote-btn" className="rounded-full bg-gold hover:bg-cream text-ink px-8 h-12 transition-colors">Get Wholesale Quote</Button>
            <a href={whatsappLink()} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full border-cream/40 text-cream hover:bg-cream hover:text-forest px-8 h-12 transition-colors" data-testid="cta-wa-btn">
                <MessageCircle size={18} className="mr-2" /> WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
